import { useState } from "react";
import api from "@/lib/api";
import { PageHeader, Spinner, EmptyState } from "@/components/Common";
import { Facebook, Sparkles, Mail, MapPin, Phone, ChefHat, Save, Info, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";

const SAMPLE = `Wir suchen ab sofort einen Koch (m/w/d) für unser Hotel Alpenblick in Garmisch!
Vollzeit, Kost & Logis frei. Erfahrung als Chef de Partie von Vorteil.
Bewerbung an: kueche@hotel-alpenblick.de  Tel. +49 8821 12345

---

Restaurant Seeblick in Konstanz sucht Sous Chef.
Bitte Lebenslauf senden an bewerbung@seeblick-konstanz.de`;

export default function FacebookImport() {
  const [content, setContent] = useState("");
  const [url, setUrl] = useState("");
  const [offers, setOffers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [extracted, setExtracted] = useState(false);

  const extract = async () => {
    if (!content.trim() && !url.trim()) { toast.error("Incolla il testo di un post o un URL"); return; }
    setLoading(true); setNote("");
    try {
      const res = await api.post("/facebook/extract", { content, url });
      setOffers(res.data.offers);
      setSelected(new Set(res.data.offers.map((_, i) => i)));
      setExtracted(true);
      if (res.data.url_note) setNote(res.data.url_note);
      if (res.data.offers.length === 0) toast.warning("Nessuna email trovata nel testo");
      else toast.success(`${res.data.offers.length} email/offerte estratte`);
    } catch (e) { toast.error("Errore durante l'estrazione"); }
    finally { setLoading(false); }
  };

  const toggle = (i) => {
    const n = new Set(selected);
    n.has(i) ? n.delete(i) : n.add(i);
    setSelected(n);
  };

  const save = async () => {
    const chosen = offers.filter((_, i) => selected.has(i));
    if (chosen.length === 0) { toast.error("Seleziona almeno un'offerta"); return; }
    setSaving(true);
    try {
      const res = await api.post("/facebook/import", { offers: chosen });
      toast.success(`Aggiunti ai lead: ${res.data.created}`, { description: `${res.data.skipped} già presenti/scartati` });
      setOffers([]); setSelected(new Set()); setContent(""); setUrl(""); setExtracted(false);
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 max-w-[1500px]">
      <PageHeader title="Import da Gruppi Facebook" subtitle="Incolla i post/annunci dei gruppi: estraggo email, ruolo, hotel, città e telefono" icon={Facebook} />

      <div className="card p-4 mb-5 flex gap-3 items-start border-sky-500/20 bg-sky-500/[0.04]">
        <Info className="w-5 h-5 text-sky-400 shrink-0 mt-0.5" />
        <p className="text-sm text-gray-300 leading-relaxed">
          Per la tua sicurezza <span className="text-gray-100 font-medium">non automatizziamo l'accesso al tuo account Facebook</span> (violerebbe i termini e rischierebbe il ban).
          Tu apri i gruppi di cui sei membro e incolli qui il testo dei post: l'app fa tutto il resto — estrazione email, deduplica e creazione lead pronti per l'invio del CV. Puoi anche incollare un link a un post pubblico.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-5 space-y-4 h-fit">
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Link post pubblico (opzionale)</label>
            <input data-testid="fb-url" value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.facebook.com/groups/.../posts/..."
              className="input-dark w-full px-3 py-2.5 mt-1.5 text-sm" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-wider text-gray-500">Testo dei post / annunci</label>
              <button onClick={() => setContent(SAMPLE)} className="text-xs text-amber-400 hover:underline" data-testid="fb-sample">Inserisci esempio</button>
            </div>
            <textarea data-testid="fb-content" value={content} onChange={(e) => setContent(e.target.value)} rows={14}
              placeholder="Incolla qui uno o più annunci copiati dai gruppi Facebook..."
              className="input-dark w-full px-3 py-2.5 mt-1.5 text-sm font-mono leading-relaxed" />
          </div>
          <button data-testid="fb-extract-btn" onClick={extract} disabled={loading}
            className="btn-copper w-full py-3 rounded-lg text-sm flex items-center justify-center gap-2">
            {loading ? <Spinner /> : <Sparkles className="w-4 h-4" />} Estrai email & offerte
          </button>
          {note && <p className="text-xs text-amber-400">{note}</p>}
        </div>

        <div className="card overflow-hidden">
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <span className="text-sm text-gray-300">{extracted ? `${offers.length} offerte estratte` : "Anteprima estrazione"}</span>
            {offers.length > 0 && (
              <button data-testid="fb-save-btn" onClick={save} disabled={saving}
                className="btn-copper px-4 py-2 rounded-lg text-sm flex items-center gap-2">
                {saving ? <Spinner /> : <Save className="w-4 h-4" />} Salva {selected.size} ai lead
              </button>
            )}
          </div>
          <div className="max-h-[560px] overflow-y-auto">
            {offers.length === 0 ? (
              <EmptyState icon={Facebook} title={extracted ? "Nessuna email trovata" : "Nessuna estrazione ancora"}
                hint="Incolla il testo dei post e premi 'Estrai email & offerte'." />
            ) : offers.map((o, i) => (
              <button key={i} onClick={() => toggle(i)} data-testid={`fb-offer-${i}`}
                className="w-full text-left flex gap-3 p-4 border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                {selected.has(i) ? <CheckSquare className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" /> : <Square className="w-4 h-4 text-gray-600 shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-100">{o.hotel_name}</div>
                  <div className="flex items-center gap-1.5 text-sm text-amber-400/90 font-mono mt-1"><Mail className="w-3.5 h-3.5" />{o.email}</div>
                  <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-gray-400">
                    <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{o.city}</span>
                    {o.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{o.phone}</span>}
                  </div>
                  {(o.roles || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {o.roles.map((r) => <span key={r} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30 flex items-center gap-1"><ChefHat className="w-3 h-3" />{r}</span>)}
                    </div>
                  )}
                  <div className="text-[11px] text-gray-500 mt-2 line-clamp-2">{o.excerpt}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
